import type { QueryClient } from '@tanstack/react-query';
import { CargoProblemWorkspaceUpdatedEventSchema } from '@magimp/shared-realtime';
import { cargoProblemsKeys } from '../features/cargo-problems/index.js';

/**
 * @aiPurpose turns the content-free `cargo-problem-workspace:updated` event
 *   into the narrowest re-read that can show the change.
 * @aiInvariant validation is mandatory and the payload is never rendered — it
 *   names a parent and a cache family, and every value the reader sees comes
 *   back from REST under server-side visibility.
 * @aiInvariant the whole cargo-problem root is never invalidated: a message
 *   must not force the list, the summary and every filter option to refetch.
 */
export function createCargoProblemWorkspaceUpdatedHandler(queryClient: QueryClient) {
  return (raw: unknown): void => {
    const parsed = CargoProblemWorkspaceUpdatedEventSchema.safeParse(raw);
    if (!parsed.success) return;
    const { problemId, kind } = parsed.data;

    if (kind === 'workflow') {
      void queryClient.invalidateQueries({ queryKey: cargoProblemsKeys.detail(problemId) });
      void queryClient.invalidateQueries({
        queryKey: cargoProblemsKeys.workspaceCapabilities(problemId),
      });
      void queryClient.invalidateQueries({ queryKey: cargoProblemsKeys.activity(problemId) });
    }
    if (kind === 'material') {
      // WHY: a deletion is an audited event, and the technical history renders
      // `ATTACHMENT_DELETED`, so the audit is as stale as the library itself.
      void queryClient.invalidateQueries({ queryKey: cargoProblemsKeys.activity(problemId) });
    }
    if (kind !== 'read') {
      // WHY: a message and a workflow move both bind their attachments in the
      // same commit, so any of the three kinds can publish a new material. The
      // library is cached per filter+search, so the id prefix covers every pane
      // the reader may currently have open.
      void queryClient.invalidateQueries({
        queryKey: [...cargoProblemsKeys.all, 'workspace-materials', problemId],
      });
      void queryClient.invalidateQueries({
        queryKey: cargoProblemsKeys.workspaceMessages(problemId),
      });
    }
    void queryClient.invalidateQueries({ queryKey: cargoProblemsKeys.attention() });
  };
}
